import {useMemo} from "react";
import {Artifact, MessageWithMetadata} from "../../../types";

/**
 * Build thread-level artifact list and compute where each artifact card should render.
 * An artifact card renders on the LAST message that references it (via [aN] in content
 * or has it in artifacts[]), so edited artifacts show on the latest message.
 */
export function useThreadArtifacts(messages: MessageWithMetadata[] | undefined) {
    return useMemo(() => {
        const seen = new Map<string, Artifact>();
        const order: string[] = [];
        for (const msg of messages ?? []) {
            for (const a of msg.artifacts ?? []) {
                if (!seen.has(a.id)) order.push(a.id);
                seen.set(a.id, a); // latest version wins
            }
        }
        const threadArtifacts = order.map(id => seen.get(id)!);

        // Find the last message position that references each artifact
        const lastRef = new Map<string, number>();
        for (const msg of messages ?? []) {
            for (const a of msg.artifacts ?? []) {
                lastRef.set(a.id, Math.max(lastRef.get(a.id) ?? -1, msg.position));
            }
            // Scan content for [aN] references
            for (const m of (msg.content ?? "").matchAll(/\[a(\d+)\]/g)) {
                const idx = parseInt(m[1], 10) - 1;
                if (idx >= 0 && idx < threadArtifacts.length) {
                    const aid = threadArtifacts[idx].id;
                    lastRef.set(aid, Math.max(lastRef.get(aid) ?? -1, msg.position));
                }
            }
        }

        // Build position -> artifacts to render as cards
        const artifactCardMap = new Map<number, Artifact[]>();
        for (const [aid, pos] of lastRef) {
            const list = artifactCardMap.get(pos) ?? [];
            list.push(seen.get(aid)!);
            artifactCardMap.set(pos, list);
        }

        return {threadArtifacts, artifactCardMap};
    }, [messages]);
}
